import ListScrollViewport from "./ListScrollViewport";
import SortableHeader from "./SortableHeader";
import "./ListTableRegion.scss";

/**
 * Plan Manager list table shell: region wrapper, optional results line, table
 * header, empty row, and footer slot. Row markup is supplied as children.
 *
 * @param {object} props
 * @param {string} [props.id] - Optional region id (e.g. tab panel id).
 * @param {string} [props.role] - Optional ARIA role for the region.
 * @param {string} [props.resultsLabel] - Optional results-found line above the table.
 * @param {string} props.tableClassName - Class applied to the `<table>` element.
 * @param {number} props.columnCount - Column count for empty-row colspan.
 * @param {boolean} props.isEmpty - Whether the table has no data rows.
 * @param {string} [props.emptyMessage] - Message shown in the empty row.
 * @param {Array<{
 *   key: string,
 *   label: string,
 *   sortable?: boolean,
 *   field?: string,
 * }>} props.columns - Table header definitions.
 * @param {string | null} [props.sortedField] - Active sort field.
 * @param {boolean} [props.isSortDescending] - Whether sort is descending.
 * @param {(field: string) => void} [props.onSort] - Sort handler for sortable columns.
 * @param {string} [props.sortButtonClassName] - Class for sortable header buttons.
 * @param {(scrollElement: HTMLElement | null) => void} [props.onScroll] - Scroll handler on the table wrapper.
 * @param {import('react').ReactNode} [props.footer] - Optional footer below the table.
 * @param {import('react').ReactNode} props.children - Table body rows.
 * @returns {import('react').JSX.Element}
 */
const ListTableRegion = ({
    id,
    role,
    resultsLabel,
    tableClassName,
    columnCount,
    isEmpty,
    emptyMessage,
    columns,
    sortedField = null,
    isSortDescending = false,
    onSort,
    sortButtonClassName,
    onScroll,
    footer,
    children,
}) => (
    <div className="plan-manager-list-table-region" id={id} role={role}>
        {resultsLabel ? (
            <p className="plan-manager-list-table-region__results">
                {resultsLabel}
            </p>
        ) : null}

        <ListScrollViewport onScroll={onScroll}>
            <table className={tableClassName}>
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th key={column.key} scope="col">
                                {column.sortable && onSort ? (
                                    <SortableHeader
                                        label={column.label}
                                        field={column.field}
                                        sortedField={sortedField}
                                        isSortDescending={isSortDescending}
                                        onSort={onSort}
                                        className={sortButtonClassName}
                                    />
                                ) : (
                                    column.label
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {isEmpty && emptyMessage ? (
                        <tr className="plan-manager-list-table-region__empty-row">
                            <td colSpan={columnCount}>{emptyMessage}</td>
                        </tr>
                    ) : null}
                    {children}
                </tbody>
            </table>
        </ListScrollViewport>

        {footer}
    </div>
);

export default ListTableRegion;
